import React, { useEffect, useState } from "react";
import Card from "../components/ui/Card";
import "./UVIndexPage.css";

type UVData = {
  liveIndex: {
    now: number;
    max: number;
    category: "Low" | "Moderate" | "High" | "Very High" | "Extreme";
  };
  currentWeather: {
    temp: number;
    windSpeed: number;
    humidity?: number; // OpenMeteo doesn't always give current humidity easily in one call, we'll try
    isDay: boolean;
  };
  daily: {
    sunrise: string;
    sunset: string;
    tempMax: number;
    tempMin: number;
  };
  hourly: Array<{ time: string; value: number }>;
  isLoading: boolean;
  error: string | null;
};

const categoryColors: Record<string, string> = {
  Low: "#56D98B",
  Moderate: "#FFBF3C",
  High: "#FF7043",
  "Very High": "#FF3B30",
  Extreme: "#9E47F5",
};

const getUVCategory = (index: number): UVData["liveIndex"]["category"] => {
  if (index <= 2) return "Low";
  if (index <= 5) return "Moderate";
  if (index <= 7) return "High";
  if (index <= 10) return "Very High";
  return "Extreme";
};

const getUVRecommendation = (uvMax: number, uvNow: number): string => {
  if (uvMax >= 11) {
    return "Extreme UV levels expected. Avoid outdoor exposure between 10am and 5pm if possible.";
  } else if (uvMax >= 8) {
    return "Very high UV levels today. Limit sun exposure during midday hours (10am - 5pm).";
  } else if (uvMax >= 6) {
    return "High UV levels expected. Sun protection strongly recommended from 10am to 3pm.";
  } else if (uvMax >= 3) {
    return "Moderate UV levels today. Some sun protection advised during peak hours.";
  } else {
    return "Low UV levels today. Minimal sun protection required, but sunscreen still recommended for extended outdoor activities.";
  }
};

const getWeatherTips = (uvMax: number, temp: number): string[] => {
  const tips: string[] = [];
  
  // UV-based tips
  if (uvMax >= 6) {
    tips.push("Apply SPF 50+ sunscreen 20 minutes before going outside and reapply every 2 hours.");
    tips.push("Wear UV-protective sunglasses and a wide-brimmed hat.");
  } else if (uvMax >= 3) {
    tips.push("Apply SPF 30+ sunscreen for extended outdoor activities.");
    tips.push("Consider wearing sunglasses and a hat during midday.");
  } else {
    tips.push("Sunscreen is optional but still beneficial for sensitive skin.");
    tips.push("Great day for outdoor activities with minimal UV risk.");
  }
  
  // Temperature-based tips
  if (temp >= 35) {
    tips.push("Stay hydrated - drink water regularly to prevent heat exhaustion.");
    tips.push("Avoid strenuous outdoor activities during the hottest part of the day.");
  } else if (temp >= 30) {
    tips.push("Keep hydrated and take breaks in the shade during outdoor activities.");
  } else if (temp <= 10) {
    tips.push("Despite cool temperatures, UV rays can still cause skin damage.");
  }
  
  return tips.slice(0, 4); // Limit to 4 tips
};

const getWeatherSummary = (tempMax: number, tempMin: number, uvMax: number, uvNow: number): string => {
  let summary = `Today's forecast: High of ${tempMax.toFixed(0)}°C, low of ${tempMin.toFixed(0)}°C. `;
  
  const tempDiff = tempMax - tempMin;
  if (tempDiff > 15) {
    summary += "Large temperature swing expected - dress in layers. ";
  }
  
  if (tempMax >= 35) {
    summary += "Hot conditions ahead. ";
  } else if (tempMax >= 28) {
    summary += "Warm and pleasant. ";
  } else if (tempMax <= 15) {
    summary += "Cool conditions expected. ";
  }
  
  if (uvMax >= 8) {
    summary += `Peak UV of ${uvMax.toFixed(1)} - take extra precautions.`;
  } else if (uvMax >= 5) {
    summary += `Moderate to high UV (${uvMax.toFixed(1)}) expected.`;
  } else {
    summary += `Low UV levels (${uvMax.toFixed(1)}) - safe for outdoor activities.`;
  }
  
  return summary;
};

const UVIndexPage: React.FC = () => {
  const [data, setData] = useState<UVData>({
    liveIndex: { now: 0, max: 0, category: "Low" },
    currentWeather: { temp: 0, windSpeed: 0, isDay: true },
    daily: { sunrise: "--:--", sunset: "--:--", tempMax: 0, tempMin: 0 },
    hourly: [],
    isLoading: true,
    error: null,
  });

  const [locationName, setLocationName] = useState("Loading location...");

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 1. Get User Location (Default to Melbourne if blocked/fails)
        let lat = -37.8136;
        let lon = 144.9631;
        let location = "Melbourne, AU";

        try {
          const pos: GeolocationPosition = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              timeout: 5000,
            });
          });
          lat = pos.coords.latitude;
          lon = pos.coords.longitude;
          location = `${lat.toFixed(2)}, ${lon.toFixed(2)}`;

          try {
            const geoResponse = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`,
              {
                headers: {
                  "User-Agent": "EarlySpot/1.0 (uv@earlyspot.app)",
                  Accept: "application/json",
                },
              }
            );

            if (geoResponse.ok) {
              const geoJson = await geoResponse.json();
              const city =
                geoJson.address?.city ??
                geoJson.address?.town ??
                geoJson.address?.suburb ??
                geoJson.address?.village;
              const state = geoJson.address?.state;
              const country = geoJson.address?.country_code?.toUpperCase();

              if (city && country) {
                location = `${city}, ${country}`;
              } else if (state && country) {
                location = `${state}, ${country}`;
              } else if (city) {
                location = city;
              }
            }
          } catch (geoErr) {
            console.warn("Reverse geocoding failed; using coordinates.", geoErr);
          }
        } catch (e) {
          console.warn("Location access denied, defaulting to Melbourne.");
        }
        
        setLocationName(location);

        // 2. Fetch Weather & UV from OpenMeteo
        const params = new URLSearchParams({
          latitude: lat.toString(),
          longitude: lon.toString(),
          current: "temperature_2m,is_day,wind_speed_10m",
          hourly: "uv_index",
          daily: "temperature_2m_max,temperature_2m_min,sunrise,sunset,uv_index_max",
          timezone: "auto",
          forecast_days: "1",
        });

        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?${params.toString()}`
        );

        if (!response.ok) throw new Error("Weather API failed");

        const json = await response.json();

        // 3. Process Data
        const currentHour = new Date().getHours();
        const uvNow = json.hourly.uv_index[currentHour] || 0;
        const uvMax = json.daily.uv_index_max[0];
        
        // Filter next 24 hours for the chart (or just today's daylight hours)
        const hourlyData = json.hourly.time
          .map((t: string, i: number) => ({
            time: new Date(t).getHours().toString().padStart(2, '0') + ":00",
            value: json.hourly.uv_index[i],
            hour: new Date(t).getHours()
          }))
          .filter((h: any) => h.hour >= 6 && h.hour <= 20) // Daylight hours roughly
          .slice(0, 12); // Limit bars

        setData({
          liveIndex: {
            now: uvNow,
            max: uvMax,
            category: getUVCategory(uvNow),
          },
          currentWeather: {
            temp: json.current.temperature_2m,
            windSpeed: json.current.wind_speed_10m,
            isDay: json.current.is_day === 1,
          },
          daily: {
            sunrise: new Date(json.daily.sunrise[0]).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
            sunset: new Date(json.daily.sunset[0]).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
            tempMax: json.daily.temperature_2m_max[0],
            tempMin: json.daily.temperature_2m_min[0],
          },
          hourly: hourlyData,
          isLoading: false,
          error: null,
        });
      } catch (err) {
        setData((prev) => ({
          ...prev,
          isLoading: false,
          error: "Failed to load weather data.",
        }));
      }
    };

    fetchData();
  }, []);

  if (data.isLoading) {
    return (
      <div className="uv uv--loading">
        <Card padding="lg">Loading UV Data...</Card>
      </div>
    );
  }

  if (data.error) {
    return (
      <div className="uv uv--error">
        <Card padding="lg">Error: {data.error}</Card>
      </div>
    );
  }

  const accent = categoryColors[data.liveIndex.category];

  return (
    <div className="uv">
      <div className="uv__grid uv__grid--top">
        <Card padding="lg" className="uv__hero" elevated>
          <div>
            <p className="uv__greeting">Good day</p>
            <h2>{locationName}</h2>
            <p className="uv__meta">{new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short' })}</p>
          </div>
          <div className="uv__divider" />
          <div className="uv__stats">
            <div className="uv__badge" style={{ background: accent }}>
              {data.liveIndex.category}
            </div>
            <div className="uv__stat">
              <span>Now</span>
              <strong>{data.liveIndex.now.toFixed(1)}</strong>
            </div>
            <div className="uv__stat">
              <span>Max</span>
              <strong>{data.liveIndex.max.toFixed(1)}</strong>
            </div>
          </div>
        </Card>
        <Card padding="lg" className="uv__conditions" elevated>
          <div className="uv__condition-row">
            <span className="uv__condition-label">Sunrise</span>
            <span>{data.daily.sunrise}</span>
          </div>
          <div className="uv__condition-row">
            <span className="uv__condition-label">Sunset</span>
            <span>{data.daily.sunset}</span>
          </div>
          <div className="uv__condition-row">
            <span className="uv__condition-label">Temp</span>
            <span>{data.currentWeather.temp}°C</span>
          </div>
          <div className="uv__condition-row">
            <span className="uv__condition-label">Wind</span>
            <span>{data.currentWeather.windSpeed} km/h</span>
          </div>
        </Card>
      </div>

      <div className="uv__grid uv__grid--bottom">
        <Card padding="md">
          <h3>Summary</h3>
          <p className="uv__summary">
            {getWeatherSummary(data.daily.tempMax, data.daily.tempMin, data.liveIndex.max, data.liveIndex.now)}
          </p>
          <p className="uv__recommendation">
            {getUVRecommendation(data.liveIndex.max, data.liveIndex.now)}
          </p>
          <ul className="uv__tips">
            {getWeatherTips(data.liveIndex.max, data.currentWeather.temp).map((tip, index) => (
              <li key={index}>{tip}</li>
            ))}
          </ul>
        </Card>
        <Card padding="md" className="uv__chart">
          <h3>Hourly UV index</h3>
          <div className="uv__bars">
            {data.hourly.map((entry) => (
              <div className="uv__bar" key={entry.time}>
                <div
                  className="uv__bar-fill"
                  style={{
                    height: `${Math.min(Math.max((entry.value / (data.liveIndex.max || 1)) * 100, 6), 100)}%`,
                    background: accent,
                  }}
                />
                <span>{entry.time}</span>
                <small>{entry.value.toFixed(1)}</small>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default UVIndexPage;